/**
 * Reflects purchased upgrades into CSS custom properties on `:root`.
 *
 * The Visible-Change channel (Принцип #9 from CLAUDE.md / doc 14):
 * each upgrade in `UPGRADES_ALPHA` may declare a `visibleChange.cssVariable`
 * + `cssValueFn` pair. This component watches `purchasedUpgrades` and
 * writes those CSS values to the root element on every change, so
 * components elsewhere (Spark.module.css, artifacts.module.css) can
 * reference them with `var(--spark-extra-glow)` etc.
 *
 * Doc 28 §A (cycles audit) — Delta-flash on purchase: когда апгрейд
 * увеличивает уровень, на 700ms бустим CSS-переменную выше итогового
 * значения, потом возвращаем к актуальному. Спарк/аура реагируют через
 * существующие transitions (600-800ms ease-out) — получается короткая
 * вспышка «оно стало больше → потом успокоилось», которая делает
 * визуальный эффект апгрейда видимым именно в момент покупки.
 *
 * Для componentFlag-апгрейдов (guides, fractal, streak-thread)
 * показывается showcase overlay — собственный slot обратной связи,
 * delta-flash там не нужен.
 *
 * Rendered once at the top of the React tree (App.tsx). Returns null —
 * pure side-effect component.
 */
import { useEffect, useRef } from 'react';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import { useGameStore } from '../stores/game.js';

/** Duration of the boost before settling to real value. */
const FLASH_BOOST_MS = 700;
/** Additive boost on top of real value (capped at 1.0). */
const FLASH_BOOST_DELTA = 0.3;

export function UpgradesVisualSync(): null {
  const purchasedUpgrades = useGameStore((s) => s.state.purchasedUpgrades);
  const prevLevelsRef = useRef<Map<string, number>>(new Map());
  // Pending settle timers per cssVariable — чтобы дважды быстро купить
  // апгрейд не оставило boosted value «застрявшим» (clear + restart).
  const flashTimersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const root = document.documentElement;
    const flashTimers = flashTimersRef.current;

    for (const upgrade of UPGRADES_ALPHA) {
      const level = purchasedUpgrades.get(upgrade.id) ?? 0;
      const prevLevel = prevLevelsRef.current.get(upgrade.id) ?? 0;
      const justPurchased = level > prevLevel;

      // (A) cssVariable + cssValueFn — числовые scaling vars
      // (u001 --spark-extra-glow, u002 --spark-core-density, и т.п.)
      const cssVar = upgrade.visibleChange.cssVariable;
      const valueFn = upgrade.visibleChange.cssValueFn;
      if (cssVar !== undefined && valueFn !== undefined) {
        const realValue = valueFn(level);

        if (justPurchased) {
          // Delta-flash: boost, потом settle через FLASH_BOOST_MS.
          const numValue = Number.parseFloat(realValue);
          if (Number.isFinite(numValue)) {
            const boosted = Math.min(1, numValue + FLASH_BOOST_DELTA).toString();
            root.style.setProperty(cssVar, boosted);

            const existing = flashTimers.get(cssVar);
            if (existing !== undefined) {
              window.clearTimeout(existing);
            }
            const tid = window.setTimeout(() => {
              root.style.setProperty(cssVar, realValue);
              flashTimers.delete(cssVar);
            }, FLASH_BOOST_MS);
            flashTimers.set(cssVar, tid);
          } else {
            root.style.setProperty(cssVar, realValue);
          }
        } else if (!flashTimers.has(cssVar)) {
          // Не перетираем boost — settle сам поставит real value.
          root.style.setProperty(cssVar, realValue);
        }
      }

      // (B) componentFlag — boolean visible flag.
      // JB doc 26 §4: 5 апгрейдов имели componentFlag, но CSS-вара
      // нигде не ставилась — флаг был «мёртвый». Теперь автоматически
      // ставим `--upgrade-flag-{flag}: 0 | 1` на :root, любой компонент
      // может реагировать через var() в своём CSS.
      const flag = upgrade.visibleChange.componentFlag;
      if (flag !== undefined) {
        const cssName = `--upgrade-flag-${flag}`;
        root.style.setProperty(cssName, level > 0 ? '1' : '0');
      }
    }

    // Снимок текущих уровней для следующего сравнения.
    prevLevelsRef.current = new Map(purchasedUpgrades);
  }, [purchasedUpgrades]);

  // Cleanup pending timers при unmount.
  useEffect(() => {
    const flashTimers = flashTimersRef.current;
    return () => {
      for (const tid of flashTimers.values()) {
        window.clearTimeout(tid);
      }
      flashTimers.clear();
    };
  }, []);

  return null;
}
